import { useState } from 'react';
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { apiFetch } from '../../lib/api';
import { SuburbField } from '../SuburbField';
import type { Client } from '../../hooks/useClients';

/**
 * New client dialog — the fields of the legacy clients_form_view_api.php, in the
 * dialog style of the Add Job popup. Contacts, Xero defaults and rates are added
 * from Edit Client once the client exists. `clients_code` (Client Number) is left
 * out: it was empty on every legacy client and the column was dropped.
 */

const STATES = ['VIC', 'NSW', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'] as const;

type Field =
    | 'companyName'
    | 'abn'
    | 'phoneNumber'
    | 'firstName'
    | 'lastName'
    | 'emailAddress'
    | 'directPhoneNumber'
    | 'streetName'
    | 'suburbName'
    | 'postcode'
    | 'state'
    | 'status';

function Section({ title, children }: { title: string; children: React.ReactNode }): React.JSX.Element {
    return (
        <Stack gap={1.5}>
            <Typography
                variant="subtitle2"
                sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
                {title}
            </Typography>
            {children}
        </Stack>
    );
}

interface NewClientDialogProps {
    readonly open: boolean;
    readonly onCancel: () => void;
    readonly onCreated: (client: Client) => void;
}

export function NewClientDialog({ open, onCancel, onCreated }: NewClientDialogProps): React.JSX.Element {
    const [form, setForm] = useState<Record<Field, string>>({
        companyName: '',
        abn: '',
        phoneNumber: '',
        firstName: '',
        lastName: '',
        emailAddress: '',
        directPhoneNumber: '',
        streetName: '',
        suburbName: '',
        postcode: '',
        state: 'VIC',
        status: 'Active',
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const set = (key: Field) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
    };

    function create(): void {
        setSaving(true);
        setError(null);
        const text = (v: string): string | null => (v.trim() === '' ? null : v.trim());
        const body = {
            companyName: form.companyName.trim(),
            abn: text(form.abn),
            phoneNumber: text(form.phoneNumber),
            firstName: text(form.firstName),
            lastName: text(form.lastName),
            emailAddress: text(form.emailAddress),
            directPhoneNumber: text(form.directPhoneNumber),
            streetName: text(form.streetName),
            suburbName: text(form.suburbName),
            postcode: text(form.postcode),
            state: text(form.state),
            isActive: form.status === 'Active',
        };
        apiFetch<Client>('/clients', { method: 'POST', body: JSON.stringify(body) })
            .then(onCreated)
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not create the client');
            })
            .finally(() => {
                setSaving(false);
            });
    }

    const missingName = form.companyName.trim() === '';

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onCancel}
            maxWidth="md"
            fullWidth
            slotProps={{ paper: { elevation: 1 } }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Add Client
                <IconButton onClick={onCancel} size="small" disabled={saving} aria-label="Close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Stack gap={3}>
                    {error !== null ? <Alert severity="error">{error}</Alert> : null}

                    <Section title="Company">
                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                            <TextField
                                label="Company"
                                size="small"
                                fullWidth
                                required
                                autoFocus
                                value={form.companyName}
                                onChange={set('companyName')}
                            />
                            <TextField
                                select
                                label="Status"
                                size="small"
                                sx={{ width: { sm: 180 } }}
                                value={form.status}
                                onChange={set('status')}
                            >
                                <MenuItem value="Active">Active</MenuItem>
                                <MenuItem value="Disabled">Disabled</MenuItem>
                            </TextField>
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                            <TextField
                                label="ABN"
                                size="small"
                                fullWidth
                                value={form.abn}
                                onChange={set('abn')}
                                helperText="11 digits"
                            />
                            <TextField
                                label="Phone number"
                                size="small"
                                fullWidth
                                value={form.phoneNumber}
                                onChange={set('phoneNumber')}
                            />
                        </Stack>
                    </Section>

                    <Section title="Address">
                        <TextField label="Street" size="small" value={form.streetName} onChange={set('streetName')} />
                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                            <SuburbField
                                value={form.suburbName}
                                onChange={(v) => {
                                    setForm((f) => ({ ...f, suburbName: v }));
                                }}
                                onSelect={(m) => {
                                    setForm((f) => ({
                                        ...f,
                                        suburbName: m.suburb,
                                        postcode: m.postcode,
                                        state: m.state,
                                    }));
                                }}
                            />
                            <TextField
                                label="Postcode"
                                size="small"
                                sx={{ width: { sm: 140 } }}
                                value={form.postcode}
                                onChange={set('postcode')}
                                slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 4 } }}
                            />
                            <TextField
                                select
                                label="State"
                                size="small"
                                sx={{ width: { sm: 120 } }}
                                value={form.state}
                                onChange={set('state')}
                            >
                                {STATES.map((st) => (
                                    <MenuItem key={st} value={st}>
                                        {st}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Stack>
                    </Section>

                    <Section title="Account contact">
                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                            <TextField
                                label="First name"
                                size="small"
                                fullWidth
                                value={form.firstName}
                                onChange={set('firstName')}
                            />
                            <TextField
                                label="Last name"
                                size="small"
                                fullWidth
                                value={form.lastName}
                                onChange={set('lastName')}
                            />
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                            <TextField
                                label="Email address"
                                size="small"
                                fullWidth
                                type="email"
                                value={form.emailAddress}
                                onChange={set('emailAddress')}
                            />
                            <TextField
                                label="Direct phone number"
                                size="small"
                                fullWidth
                                value={form.directPhoneNumber}
                                onChange={set('directPhoneNumber')}
                            />
                        </Stack>
                    </Section>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                {missingName ? (
                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mr: 'auto', pl: 1 }}>
                        A company name is required
                    </Typography>
                ) : null}
                <Button onClick={onCancel} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={create}
                    disabled={saving || missingName}
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                >
                    {saving ? 'Saving...' : 'Save Client'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
